import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchClientById, updateClient, deleteClient,
  fetchClientRates, createClientRate, updateClientRate, deleteClientRate,
  fetchClientInvoices, createClientInvoice, updateClientInvoice, deleteClientInvoice,
  fetchClientInvoice,
  createClientLineItem, updateClientLineItem, deleteClientLineItem,
  fetchClientPayments, createClientPayment, updateClientPayment, deleteClientPayment,
  fetchBillingSettings,
} from '../services/api';
import { InvoicePreviewModal } from '../components/InvoiceModals';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import './Admin.css';
import './Clients.css';

function fmtCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function fmtPeriod(start, end) {
  const s = new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const e = new Date(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${s} – ${e}`;
}

function today() {
  return new Date().toISOString().split('T')[0];
}

function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function monthEnd() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return last.toISOString().split('T')[0];
}

const STATUS_LABELS = { draft: 'Draft', open: 'Open', partial: 'Partial', paid: 'Paid', void: 'Void' };
const METHOD_LABELS = { check: 'Check', ach: 'ACH', eft: 'EFT', wire: 'Wire', other: 'Other' };

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const canMove = useCanMoveMoney();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('invoices');

  const [rates, setRates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [billingSettings, setBillingSettings] = useState(null);

  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [lineItemsMap, setLineItemsMap] = useState({});
  const [lineItemsLoading, setLineItemsLoading] = useState({});

  // Preview state: { invoice, lineItems } when the preview modal is open
  const [previewInvoice, setPreviewInvoice] = useState(null);

  // Modal state: { type, mode, target, invoiceId? }
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Delete confirm: { type, target, label }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [clientRes, ratesRes, invoicesRes, paymentsRes, settingsRes] = await Promise.all([
        fetchClientById(id),
        fetchClientRates(id),
        fetchClientInvoices(id),
        fetchClientPayments(id),
        fetchBillingSettings().catch(() => ({ data: null })),
      ]);
      setClient(clientRes.data);
      setRates(ratesRes.data || []);
      setInvoices(invoicesRes.data || []);
      setPayments(paymentsRes.data || []);
      setBillingSettings(settingsRes.data || null);
    } catch (err) {
      console.error('Failed to load client:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshClient = useCallback(async () => {
    const res = await fetchClientById(id);
    setClient(res.data);
  }, [id]);

  // ── Invoice expand ─────────────────────────────────────────────────────────

  const toggleInvoice = async (invId) => {
    if (expandedInvoiceId === invId) { setExpandedInvoiceId(null); return; }
    setExpandedInvoiceId(invId);
    if (lineItemsMap[invId]) return;
    setLineItemsLoading(prev => ({ ...prev, [invId]: true }));
    try {
      const res = await fetchClientInvoice(id, invId);
      setLineItemsMap(prev => ({ ...prev, [invId]: res.data.line_items || [] }));
    } catch (err) {
      console.error(err);
    } finally {
      setLineItemsLoading(prev => ({ ...prev, [invId]: false }));
    }
  };

  const refreshInvoice = async (invId) => {
    const [invRes, lineRes] = await Promise.all([
      fetchClientInvoices(id),
      fetchClientInvoice(id, invId),
    ]);
    setInvoices(invRes.data || []);
    setLineItemsMap(prev => ({ ...prev, [invId]: lineRes.data.line_items || [] }));
    refreshClient();
  };

  const refreshInvoiceList = async () => {
    const res = await fetchClientInvoices(id);
    setInvoices(res.data || []);
    refreshClient();
  };

  const refreshPayments = async () => {
    const res = await fetchClientPayments(id);
    setPayments(res.data || []);
    refreshClient();
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const openModal = (type, mode, target, extra) => {
    setModalError(null);
    if (type === 'rate') {
      setForm(mode === 'edit'
        ? { service_name: target.service_name, cpt_code: target.cpt_code || '', rate: target.rate, notes: target.notes || '' }
        : { service_name: '', cpt_code: '', rate: '', notes: '' });
    } else if (type === 'invoice') {
      setForm({ period_start: monthStart(), period_end: monthEnd(), invoice_date: today(), due_date: '', notes: '' });
    } else if (type === 'lineItem') {
      setForm(mode === 'edit'
        ? { service_date: target.service_date?.split('T')[0], service_name: target.service_name, cpt_code: target.cpt_code || '', mrn: target.mrn || '', description: target.description || '', quantity: target.quantity, unit_rate: target.unit_rate }
        : { service_date: today(), service_name: '', cpt_code: '', mrn: '', description: '', quantity: 1, unit_rate: '' });
    } else if (type === 'payment') {
      setForm(mode === 'edit'
        ? { payment_date: target.payment_date?.split('T')[0], amount: target.amount, payment_method: target.payment_method, reference_number: target.reference_number || '', invoice_id: target.invoice_id || '', notes: target.notes || '' }
        : { payment_date: today(), amount: '', payment_method: 'check', reference_number: '', invoice_id: extra?.invoiceId || '', notes: '' });
    } else if (type === 'editClient') {
      setForm({
        name: client.name, invoice_prefix: client.invoice_prefix,
        contact_name: client.contact_name || '', contact_email: client.contact_email || '',
        contact_phone: client.contact_phone || '', address: client.address || '',
        city: client.city || '', state: client.state || '', zip: client.zip || '',
        payment_terms: client.payment_terms || 'Net 30', notes: client.notes || '',
        status: client.status,
      });
    }
    setModal({ type, mode, target, ...extra });
  };

  const closeModal = () => { setModal(null); setForm({}); setModalError(null); };

  // ── Save handlers ──────────────────────────────────────────────────────────

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setModalError(null);
    try {
      if (modal.type === 'rate') {
        if (modal.mode === 'create') {
          const res = await createClientRate(id, form);
          setRates(prev => [...prev, res.data].sort((a, b) => a.service_name.localeCompare(b.service_name)));
        } else {
          const res = await updateClientRate(id, modal.target.rate_id, form);
          setRates(prev => prev.map(r => r.rate_id === modal.target.rate_id ? res.data : r));
        }
        closeModal();
      } else if (modal.type === 'invoice') {
        const res = await createClientInvoice(id, form);
        setInvoices(prev => [res.data, ...prev]);
        refreshClient();
        closeModal();
      } else if (modal.type === 'lineItem') {
        if (modal.mode === 'create') {
          const res = await createClientLineItem(id, modal.invoiceId, form);
          setLineItemsMap(prev => ({
            ...prev,
            [modal.invoiceId]: [...(prev[modal.invoiceId] || []), res.data],
          }));
        } else {
          const res = await updateClientLineItem(id, modal.invoiceId, modal.target.line_item_id, form);
          setLineItemsMap(prev => ({
            ...prev,
            [modal.invoiceId]: (prev[modal.invoiceId] || []).map(li =>
              li.line_item_id === modal.target.line_item_id ? res.data : li
            ),
          }));
        }
        await refreshInvoice(modal.invoiceId);
        closeModal();
      } else if (modal.type === 'payment') {
        if (modal.mode === 'create') {
          await createClientPayment(id, form);
        } else {
          await updateClientPayment(id, modal.target.payment_id, form);
        }
        await refreshPayments();
        if (form.invoice_id) await refreshInvoice(parseInt(form.invoice_id, 10));
        closeModal();
      } else if (modal.type === 'editClient') {
        const res = await updateClient(id, form);
        setClient(res.data);
        closeModal();
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) setModalError('A rate with this service name already exists.');
      else setModalError(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { type, target } = confirmDelete;
      if (type === 'rate') {
        await deleteClientRate(id, target.rate_id);
        setRates(prev => prev.filter(r => r.rate_id !== target.rate_id));
      } else if (type === 'invoice') {
        await deleteClientInvoice(id, target.invoice_id);
        setInvoices(prev => prev.filter(i => i.invoice_id !== target.invoice_id));
        if (expandedInvoiceId === target.invoice_id) setExpandedInvoiceId(null);
        refreshClient();
      } else if (type === 'lineItem') {
        await deleteClientLineItem(id, confirmDelete.invoiceId, target.line_item_id);
        setLineItemsMap(prev => ({
          ...prev,
          [confirmDelete.invoiceId]: (prev[confirmDelete.invoiceId] || []).filter(li => li.line_item_id !== target.line_item_id),
        }));
        await refreshInvoice(confirmDelete.invoiceId);
      } else if (type === 'payment') {
        await deleteClientPayment(id, target.payment_id);
        await refreshPayments();
      } else if (type === 'client') {
        await deleteClient(id);
        navigate('/clients');
        return;
      }
      setConfirmDelete(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Invoice status change (inline) ─────────────────────────────────────────

  const handleStatusChange = async (invoice, newStatus) => {
    try {
      const res = await updateClientInvoice(id, invoice.invoice_id, {
        period_start: invoice.period_start?.split('T')[0],
        period_end: invoice.period_end?.split('T')[0],
        invoice_date: invoice.invoice_date?.split('T')[0],
        status: newStatus,
        notes: invoice.notes,
      });
      setInvoices(prev => prev.map(i => i.invoice_id === invoice.invoice_id ? { ...i, ...res.data } : i));
    } catch (err) {
      console.error('Status change failed:', err);
    }
  };

  // ── Line item service name → auto-fill rate ────────────────────────────────

  const handleLineItemServiceChange = (val) => {
    setField('service_name', val);
    const match = rates.find(r => r.service_name.toLowerCase() === val.toLowerCase());
    if (match) {
      setField('unit_rate', match.rate);
      if (match.cpt_code) setField('cpt_code', match.cpt_code);
    }
  };

  // ── Computed totals ────────────────────────────────────────────────────────

  const totalBilled = parseFloat(client?.total_billed ?? 0);
  const totalPaid = parseFloat(client?.total_paid ?? 0);
  const balance = totalBilled - totalPaid;

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="admin-page">
        <div className="admin-empty"><h3>Client not found</h3></div>
      </div>
    );
  }

  return (
    <div className="admin-page client-detail-page">
      {/* Header */}
      <div className="client-detail-header">
        <button className="client-back-btn" onClick={() => navigate('/clients')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Clients
        </button>

        <div className="client-header-main">
          <div className="client-header-identity">
            <div className="client-header-avatar">{client.name.slice(0, 2).toUpperCase()}</div>
            <div>
              <h1 className="client-header-name">{client.name}</h1>
              <div className="client-header-meta">
                <span className="clients-facility-badge">{client.facility_name}</span>
                <span className={`admin-status-badge ${client.status}`}>
                  <span className="status-indicator" />
                  {client.status === 'active' ? 'Active' : 'Inactive'}
                </span>
                {client.payment_terms && (
                  <span className="client-terms-badge">{client.payment_terms}</span>
                )}
              </div>
            </div>
          </div>

          <div className="client-header-actions">
            <button className="client-action-btn" onClick={() => openModal('editClient', 'edit', null)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
            <button
              className="client-action-btn danger"
              onClick={() => setConfirmDelete({ type: 'client', target: client, label: client.name })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="client-stats-row">
        <div className="client-stat-card">
          <div className="client-stat-label">Total Billed</div>
          <div className="client-stat-value billed">{fmtCurrency(totalBilled)}</div>
          <div className="client-stat-sub">{invoices.filter(i => i.status !== 'void').length} invoice{invoices.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="client-stat-card">
          <div className="client-stat-label">Total Paid</div>
          <div className="client-stat-value paid">{fmtCurrency(totalPaid)}</div>
          <div className="client-stat-sub">{payments.length} payment{payments.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="client-stat-card">
          <div className="client-stat-label">Balance Due</div>
          <div className={`client-stat-value ${balance > 0 ? 'balance-due' : 'balance-clear'}`}>
            {fmtCurrency(balance)}
          </div>
          <div className="client-stat-sub">
            {client.open_invoice_count > 0
              ? `${client.open_invoice_count} open invoice${client.open_invoice_count !== 1 ? 's' : ''}`
              : 'No open invoices'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="client-tabs">
        {[
          { key: 'invoices', label: 'Invoices' },
          { key: 'payments', label: 'Payments' },
          { key: 'fee-schedule', label: 'Fee Schedule' },
        ].map(t => (
          <button
            key={t.key}
            className={`client-tab-btn ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'invoices' && invoices.length > 0 && (
              <span className="client-tab-count">{invoices.length}</span>
            )}
            {t.key === 'payments' && payments.length > 0 && (
              <span className="client-tab-count">{payments.length}</span>
            )}
            {t.key === 'fee-schedule' && rates.length > 0 && (
              <span className="client-tab-count">{rates.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── INVOICES TAB ──────────────────────────────────────────────────────── */}
      {tab === 'invoices' && (
        <div className="client-tab-content">
          <div className="client-tab-toolbar">
            <span className="admin-count">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="client-action-btn primary" onClick={() => { openModal('payment', 'create', null, {}); }} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Log Payment
              </button>
              <button className="admin-create-btn" onClick={() => openModal('invoice', 'create', null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create Invoice
              </button>
            </div>
          </div>

          {invoices.length === 0 ? (
            <div className="admin-table-card">
              <div className="admin-empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <h3>No invoices yet</h3>
                <p>Create an invoice to start tracking services for this client</p>
              </div>
            </div>
          ) : (
            <div className="admin-table-card">
              <table className="admin-table client-invoices-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }} />
                    <th>Invoice #</th>
                    <th>Period</th>
                    <th>Items</th>
                    <th className="num">Total</th>
                    <th className="num">Paid</th>
                    <th className="num">Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const invTotal = parseFloat(inv.total_amount ?? 0);
                    const invPaid = parseFloat(inv.amount_paid ?? 0);
                    const invBalance = invTotal - invPaid;
                    const isExpanded = expandedInvoiceId === inv.invoice_id;
                    const items = lineItemsMap[inv.invoice_id] || [];

                    return (
                      <>
                        <tr
                          key={inv.invoice_id}
                          className={`invoice-row ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleInvoice(inv.invoice_id)}
                        >
                          <td className="invoice-chevron-cell">
                            <span className={`invoice-chevron ${isExpanded ? 'open' : ''}`}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </span>
                          </td>
                          <td>
                            <span className="invoice-number">{inv.invoice_number}</span>
                          </td>
                          <td className="invoice-period">
                            {fmtPeriod(inv.period_start, inv.period_end)}
                          </td>
                          <td>
                            <span className={`payer-patients-badge ${inv.line_item_count > 0 ? 'has-patients' : ''}`}>
                              {inv.line_item_count}
                            </span>
                          </td>
                          <td className="num">{fmtCurrency(invTotal)}</td>
                          <td className="num" style={{ color: '#15803d', fontWeight: 500 }}>{fmtCurrency(invPaid)}</td>
                          <td className="num">
                            <span className={`clients-balance ${invBalance > 0 ? 'positive' : 'zero'}`}>
                              {fmtCurrency(invBalance)}
                            </span>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <select
                              className={`invoice-status-select ${inv.status}`}
                              value={inv.status}
                              onChange={e => handleStatusChange(inv, e.target.value)}
                            >
                              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="payer-actions">
                              <button
                                className="admin-action-btn payer-edit-btn"
                                onClick={async () => {
                                  // Ensure line items are loaded before opening preview
                                  let items = lineItemsMap[inv.invoice_id];
                                  if (!items) {
                                    const res = await fetchClientInvoice(id, inv.invoice_id);
                                    items = res.data.line_items || [];
                                    setLineItemsMap(prev => ({ ...prev, [inv.invoice_id]: items }));
                                  }
                                  setPreviewInvoice({ invoice: inv, lineItems: items });
                                }}
                                title="Preview, download, or send the invoice PDF"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                </svg>
                                View
                              </button>
                              <button
                                className="admin-action-btn payer-edit-btn"
                                onClick={() => openModal('payment', 'create', null, { invoiceId: inv.invoice_id })}
                                disabled={!canMove}
                                title={canMove ? 'Log payment against this invoice' : MONEY_ROLE_HINT}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                                  <line x1="1" y1="10" x2="23" y2="10" />
                                </svg>
                                Pay
                              </button>
                              {['draft', 'void'].includes(inv.status) && (
                                <button
                                  className="admin-action-btn deactivate"
                                  onClick={() => setConfirmDelete({ type: 'invoice', target: inv, label: inv.invoice_number })}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                    <path d="M10 11v6M14 11v6" />
                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                  </svg>
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded line items */}
                        {isExpanded && (
                          <tr key={`${inv.invoice_id}-expanded`} className="invoice-expanded-row">
                            <td colSpan={9} className="invoice-expanded-cell">
                              {lineItemsLoading[inv.invoice_id] ? (
                                <div style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}>
                                  <div className="loading-spinner" />
                                </div>
                              ) : (
                                <div className="line-items-section">
                                  <div className="line-items-header">
                                    <span className="line-items-title">Line Items</span>
                                    {inv.status === 'draft' ? (
                                      <button
                                        className="line-items-add-btn"
                                        onClick={() => openModal('lineItem', 'create', null, { invoiceId: inv.invoice_id })}
                                      >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        Add Line Item
                                      </button>
                                    ) : (
                                      <span className="line-items-lock">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                          <path d="M7 11V7a5 5 0 0110 0v4" />
                                        </svg>
                                        Locked — void invoice to make corrections
                                      </span>
                                    )}
                                  </div>

                                  {items.length === 0 ? (
                                    <div className="line-items-empty">No line items yet. Add the first one above.</div>
                                  ) : (
                                    <table className="line-items-table">
                                      <thead>
                                        <tr>
                                          <th>Date</th>
                                          <th>MRN</th>
                                          <th>Service</th>
                                          <th>CPT</th>
                                          <th>Description</th>
                                          <th>Qty</th>
                                          <th className="num">Unit Rate</th>
                                          <th className="num">Total</th>
                                          <th />
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {items.map(li => (
                                          <tr key={li.line_item_id}>
                                            <td>{fmtDate(li.service_date)}</td>
                                            <td>{li.mrn ? <span className="mrn-badge">{li.mrn}</span> : <span style={{ color: '#d1d5db' }}>N/A</span>}</td>
                                            <td><strong>{li.service_name}</strong></td>
                                            <td>{li.cpt_code ? <span className="cpt-badge">{li.cpt_code}</span> : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                            <td style={{ color: '#6b7280' }}>{li.description || '—'}</td>
                                            <td>{li.quantity}</td>
                                            <td className="num">{fmtCurrency(li.unit_rate)}</td>
                                            <td className="num" style={{ fontWeight: 600 }}>{fmtCurrency(li.line_total)}</td>
                                            <td>
                                              {inv.status === 'draft' && (
                                                <div className="payer-actions line-item-actions">
                                                  <button
                                                    className="client-action-btn line-item-icon-btn"
                                                    title="Edit"
                                                    onClick={() => openModal('lineItem', 'edit', li, { invoiceId: inv.invoice_id })}
                                                  >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                  </button>
                                                  <button
                                                    className="client-action-btn danger line-item-icon-btn"
                                                    title="Delete"
                                                    onClick={() => setConfirmDelete({ type: 'lineItem', target: li, label: li.service_name, invoiceId: inv.invoice_id })}
                                                  >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                      <polyline points="3 6 5 6 21 6" />
                                                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                      <path d="M10 11v6M14 11v6" />
                                                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                    </svg>
                                                  </button>
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot>
                                        <tr className="line-items-total-row">
                                          <td colSpan={6} />
                                          <td className="line-items-total-label num">Invoice Total</td>
                                          <td className="line-items-total-amount num">{fmtCurrency(invTotal)}</td>
                                          <td />
                                        </tr>
                                      </tfoot>
                                    </table>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PAYMENTS TAB ─────────────────────────────────────────────────────── */}
      {tab === 'payments' && (
        <div className="client-tab-content">
          <div className="client-tab-toolbar">
            <span className="admin-count">{payments.length} payment{payments.length !== 1 ? 's' : ''}</span>
            <button className="admin-create-btn" onClick={() => openModal('payment', 'create', null, {})} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Log Payment
            </button>
          </div>

          {payments.length === 0 ? (
            <div className="admin-table-card">
              <div className="admin-empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                <h3>No payments recorded</h3>
                <p>Log a payment when you receive one from this client</p>
              </div>
            </div>
          ) : (
            <div className="admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="num">Amount</th>
                    <th>Method</th>
                    <th>Reference #</th>
                    <th>Applied to Invoice</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(pmt => (
                    <tr key={pmt.payment_id}>
                      <td className="admin-date-cell">{fmtDate(pmt.payment_date)}</td>
                      <td className="num" style={{ fontWeight: 600, color: '#15803d' }}>{fmtCurrency(pmt.amount)}</td>
                      <td>
                        <span className="payment-method-badge">{METHOD_LABELS[pmt.payment_method] || pmt.payment_method}</span>
                      </td>
                      <td>
                        {pmt.reference_number
                          ? <span className="payer-code-badge">{pmt.reference_number}</span>
                          : <span className="payer-no-value">—</span>}
                      </td>
                      <td>
                        {pmt.invoice_number
                          ? <span className="invoice-number">{pmt.invoice_number}</span>
                          : <span className="payer-no-value unallocated">Unallocated</span>}
                      </td>
                      <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{pmt.notes || '—'}</td>
                      <td>
                        <div className="payer-actions">
                          <button className="client-action-btn line-item-icon-btn" title={canMove ? 'Edit' : MONEY_ROLE_HINT} disabled={!canMove} onClick={() => openModal('payment', 'edit', pmt, {})}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button className="client-action-btn danger line-item-icon-btn" title={canMove ? 'Delete' : MONEY_ROLE_HINT} disabled={!canMove} onClick={() => setConfirmDelete({ type: 'payment', target: pmt, label: `${fmtCurrency(pmt.amount)} on ${fmtDate(pmt.payment_date)}` })}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── FEE SCHEDULE TAB ─────────────────────────────────────────────────── */}
      {tab === 'fee-schedule' && (
        <div className="client-tab-content">
          <div className="client-tab-toolbar">
            <span className="admin-count">{rates.length} service{rates.length !== 1 ? 's' : ''}</span>
            <button className="admin-create-btn" onClick={() => openModal('rate', 'create', null)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Service
            </button>
          </div>

          {rates.length === 0 ? (
            <div className="admin-table-card">
              <div className="admin-empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                <h3>No fee schedule yet</h3>
                <p>Add service rates to auto-fill prices when entering line items</p>
              </div>
            </div>
          ) : (
            <div className="admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th className="num">Rate</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map(rate => (
                    <tr key={rate.rate_id}>
                      <td style={{ fontWeight: 600 }}>{rate.service_name}</td>
                      <td className="num" style={{ fontWeight: 700, fontSize: '1rem', color: '#171363' }}>{fmtCurrency(rate.rate)}</td>
                      <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{rate.notes || '—'}</td>
                      <td>
                        <div className="payer-actions">
                          <button className="client-action-btn line-item-icon-btn" title="Edit" onClick={() => openModal('rate', 'edit', rate)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button className="client-action-btn danger line-item-icon-btn" title="Delete" onClick={() => setConfirmDelete({ type: 'rate', target: rate, label: rate.service_name })}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── MODALS ────────────────────────────────────────────────────────────── */}
      {modal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal clients-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-icon">
                {modal.type === 'rate' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                )}
                {modal.type === 'invoice' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                )}
                {modal.type === 'lineItem' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                )}
                {(modal.type === 'payment' || modal.type === 'editClient') && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                )}
              </div>
              <div className="admin-modal-header-text">
                <h2>
                  {modal.type === 'rate' && (modal.mode === 'create' ? 'Add Service Rate' : 'Edit Service Rate')}
                  {modal.type === 'invoice' && 'Create Invoice'}
                  {modal.type === 'lineItem' && (modal.mode === 'create' ? 'Add Line Item' : 'Edit Line Item')}
                  {modal.type === 'payment' && (modal.mode === 'create' ? 'Log Payment' : 'Edit Payment')}
                  {modal.type === 'editClient' && 'Edit Client'}
                </h2>
                <p style={{ color: '#9ca3af', fontSize: '0.8125rem', margin: 0 }}>
                  {modal.type === 'rate' && `${client.name} fee schedule`}
                  {modal.type === 'invoice' && `Invoice number will be auto-generated`}
                  {modal.type === 'lineItem' && 'Select a service to auto-fill the rate'}
                  {modal.type === 'payment' && client.name}
                  {modal.type === 'editClient' && 'Update client information'}
                </p>
              </div>
            </div>

            {modalError && (
              <div className="admin-modal-alert error" style={{ marginBottom: 18 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleSave} className="admin-modal-form">
              {/* Rate form */}
              {modal.type === 'rate' && (
                <>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Service Name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" className="admin-modal-input" placeholder="e.g. Lab, X-Ray, MRI" autoFocus value={form.service_name || ''} onChange={e => setField('service_name', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>CPT Code</label>
                      <input type="text" className="admin-modal-input" placeholder="e.g. 80053" maxLength={10} value={form.cpt_code || ''} onChange={e => setField('cpt_code', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Rate <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="number" step="0.01" min="0" className="admin-modal-input" placeholder="0.00" value={form.rate || ''} onChange={e => setField('rate', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Notes</label>
                      <input type="text" className="admin-modal-input" placeholder="Optional notes" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {/* Invoice form */}
              {modal.type === 'invoice' && (
                <>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Period Start <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="date" className="admin-modal-input" value={form.period_start || ''} onChange={e => setField('period_start', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Period End <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="date" className="admin-modal-input" value={form.period_end || ''} onChange={e => setField('period_end', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Invoice Date</label>
                      <input type="date" className="admin-modal-input" value={form.invoice_date || ''} onChange={e => setField('invoice_date', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Due Date <span style={{ color: '#9ca3af', fontWeight: 400 }}>(auto from terms)</span></label>
                      <input type="date" className="admin-modal-input" value={form.due_date || ''} onChange={e => setField('due_date', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-field">
                    <label>Notes</label>
                    <input type="text" className="admin-modal-input" placeholder="Optional notes" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} />
                  </div>
                </>
              )}

              {/* Line Item form */}
              {modal.type === 'lineItem' && (
                <>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Service Date <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="date" className="admin-modal-input" autoFocus value={form.service_date || ''} onChange={e => setField('service_date', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Quantity</label>
                      <input type="number" min="1" step="1" className="admin-modal-input" value={form.quantity || 1} onChange={e => setField('quantity', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Service Type <span style={{ color: '#ef4444' }}>*</span></label>
                      {rates.length > 0 ? (
                        <select
                          className="admin-modal-select"
                          value={form.service_name || ''}
                          onChange={e => handleLineItemServiceChange(e.target.value)}
                        >
                          <option value="">Select or type below...</option>
                          {rates.map(r => (
                            <option key={r.rate_id} value={r.service_name}>{r.service_name} — {fmtCurrency(r.rate)}</option>
                          ))}
                          <option value="__custom">Custom (enter below)</option>
                        </select>
                      ) : null}
                      {(rates.length === 0 || form.service_name === '__custom' || !rates.find(r => r.service_name === form.service_name)) && (
                        <input
                          type="text"
                          className="admin-modal-input"
                          style={{ marginTop: rates.length > 0 ? 8 : 0 }}
                          placeholder="Service name"
                          value={form.service_name === '__custom' ? '' : (form.service_name || '')}
                          onChange={e => setField('service_name', e.target.value)}
                        />
                      )}
                    </div>
                    <div className="admin-modal-field">
                      <label>Unit Rate <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="admin-modal-input"
                        placeholder="0.00"
                        value={form.unit_rate || ''}
                        onChange={e => setField('unit_rate', e.target.value)}
                      />
                    </div>
                  </div>
                  {form.unit_rate && form.quantity && (
                    <div className="line-item-total-preview">
                      Line Total: <strong>{fmtCurrency(parseFloat(form.unit_rate) * parseInt(form.quantity, 10))}</strong>
                    </div>
                  )}
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>CPT Code</label>
                      <input type="text" className="admin-modal-input" placeholder="e.g. 80053" maxLength={10} value={form.cpt_code || ''} onChange={e => setField('cpt_code', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>MRN</label>
                      <input type="text" className="admin-modal-input" placeholder="Patient MRN (optional)" maxLength={50} value={form.mrn || ''} onChange={e => setField('mrn', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-field">
                    <label>Description / Notes</label>
                    <input type="text" className="admin-modal-input" placeholder="Optional description" value={form.description || ''} onChange={e => setField('description', e.target.value)} />
                  </div>
                </>
              )}

              {/* Payment form */}
              {modal.type === 'payment' && (
                <>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Payment Date <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="date" className="admin-modal-input" autoFocus value={form.payment_date || ''} onChange={e => setField('payment_date', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Amount <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="number" step="0.01" min="0" className="admin-modal-input" placeholder="0.00" value={form.amount || ''} onChange={e => setField('amount', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Payment Method</label>
                      <select className="admin-modal-select" value={form.payment_method || 'check'} onChange={e => setField('payment_method', e.target.value)}>
                        {Object.entries(METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div className="admin-modal-field">
                      <label>Reference # (Check / ACH)</label>
                      <input type="text" className="admin-modal-input" placeholder="Check # or ACH ref" value={form.reference_number || ''} onChange={e => setField('reference_number', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-field">
                    <label>Apply to Invoice <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                    <select
                      className="admin-modal-select"
                      value={form.invoice_id || ''}
                      onChange={e => setField('invoice_id', e.target.value)}
                    >
                      <option value="">Unallocated</option>
                      {invoices
                        .filter(i => !['paid', 'void'].includes(i.status))
                        .map(i => (
                          <option key={i.invoice_id} value={i.invoice_id}>
                            {i.invoice_number} — {fmtPeriod(i.period_start, i.period_end)} (Balance: {fmtCurrency(parseFloat(i.total_amount ?? 0) - parseFloat(i.amount_paid ?? 0))})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="admin-modal-field">
                    <label>Notes</label>
                    <input type="text" className="admin-modal-input" placeholder="Optional notes" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} />
                  </div>
                </>
              )}

              {/* Edit Client form */}
              {modal.type === 'editClient' && (
                <>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Client Name <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" className="admin-modal-input" autoFocus value={form.name || ''} onChange={e => setField('name', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Invoice Prefix <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" className="admin-modal-input" maxLength={10} value={form.invoice_prefix || ''} onChange={e => setField('invoice_prefix', e.target.value.toUpperCase())} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Payment Terms</label>
                      <input type="text" className="admin-modal-input" value={form.payment_terms || ''} onChange={e => setField('payment_terms', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Contact Name</label>
                      <input type="text" className="admin-modal-input" value={form.contact_name || ''} onChange={e => setField('contact_name', e.target.value)} />
                    </div>
                    <div className="admin-modal-field">
                      <label>Contact Phone</label>
                      <input type="text" className="admin-modal-input" value={form.contact_phone || ''} onChange={e => setField('contact_phone', e.target.value)} />
                    </div>
                  </div>
                  <div className="admin-modal-field">
                    <label>Contact Email</label>
                    <input type="email" className="admin-modal-input" value={form.contact_email || ''} onChange={e => setField('contact_email', e.target.value)} />
                  </div>
                  <div className="admin-modal-row">
                    <div className="admin-modal-field">
                      <label>Status</label>
                      <select className="admin-modal-select" value={form.status || 'active'} onChange={e => setField('status', e.target.value)}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div className="admin-modal-field">
                      <label>Notes</label>
                      <input type="text" className="admin-modal-input" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              <div className="admin-modal-actions">
                <button type="button" className="admin-modal-btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="admin-modal-btn-submit" disabled={saving}>
                  {saving && <span className="admin-modal-spinner" />}
                  {saving ? 'Saving...' : modal.mode === 'create' ? 'Save' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Preview / Send Modal */}
      {previewInvoice && (
        <InvoicePreviewModal
          invoice={previewInvoice.invoice}
          lineItems={previewInvoice.lineItems}
          client={client}
          billingSettings={billingSettings}
          onClose={() => setPreviewInvoice(null)}
          onSent={(updated) => {
            setPreviewInvoice(null);
            // Refresh invoice list to pick up status change + sent_at
            refreshInvoiceList();
            if (updated?.invoice_id) {
              setInvoices(prev => prev.map(i =>
                i.invoice_id === updated.invoice_id ? { ...i, ...updated } : i
              ));
            }
          }}
        />
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>
              {confirmDelete.type === 'client' ? 'Delete Client?' : `Delete ${confirmDelete.type === 'rate' ? 'Rate' : confirmDelete.type === 'invoice' ? 'Invoice' : confirmDelete.type === 'lineItem' ? 'Line Item' : 'Payment'}?`}
            </h3>
            <p>
              Are you sure you want to delete <strong>{confirmDelete.label}</strong>?
              {confirmDelete.type === 'client' && ' All invoices, line items, and payments for this client will also be deleted.'}
              {confirmDelete.type === 'invoice' && ' All line items in this invoice will also be deleted.'}
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</button>
              <button className="admin-confirm-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDetail;
